import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { PipelineName, SegmentationModel } from "./helpers/segmentationHelper";
import { BackgroundType } from "./helpers/backgroundHelper";
import { persistedRef } from "@/utils";

const allClearMethods = [];
const blurAmount = ref(15);
const backgroundPipeline = ref<PipelineName>("webgl2");
const backgroundSegmentationModel = ref<SegmentationModel>("meet");
const scale = ref(0.2);
const backgroundOperation = ref<BackgroundType>("image");
const backgroundUrl = ref("");
const backgroundActive = ref(false);
// Off-main-thread inference (5a). Default ON: inline inference blocks the main thread (~15-25ms/frame),
// making canvas.captureStream emit irregularly-spaced frames -> stutter; the worker keeps the main thread
// free so the outgoing stream is evenly paced (measured smoother). Legacy bg configs without the key stay ON via `??`.
// Disable per-scope with backgroundInferenceWorker:false in the settings blob, or fleet-wide via CONFIG.BG_INFERENCE_WORKER=false.
const backgroundInferenceWorker = ref(true);

const [sigmaColor, sigmaColorClear] = persistedRef("background.sigmaColor", 0.1);
allClearMethods.push(sigmaColorClear);

const [sigmaSpace, sigmaSpaceClear] = persistedRef("background.sigmaSpace", 1);
allClearMethods.push(sigmaSpaceClear);

const [blendMode, blendModeClear] = persistedRef<"screen" | "linearDodge">("background.blendMode", "screen");
allClearMethods.push(blendModeClear);

const [coverageMin, coverageMinClear] = persistedRef("background.coverageMin", 0.5);
allClearMethods.push(coverageMinClear);

const [coverageMax, coverageMaxClear] = persistedRef("background.coverageMax", 0.75);
allClearMethods.push(coverageMaxClear);

const [lightWrapping, lightWrappingClear] = persistedRef("background.lightWrapping", 0.3);
allClearMethods.push(lightWrappingClear);

const [smoothSegmentationMask, smoothSegmentationMaskClear] = persistedRef("background.smoothSegmentationMask", true);
allClearMethods.push(smoothSegmentationMaskClear);

const [quality, qualtyClear] = persistedRef<"high" | "medium">("background.quality", "high");
allClearMethods.push(qualtyClear);

export function clearBgBlurState() {
  allClearMethods.forEach((clear) => clear());
}

watch(
  [backgroundSegmentationModel],
  () => {
    console.log("backgroundSegmentationModel changed to", backgroundSegmentationModel.value);
  },
  { immediate: true },
);

watch(
  [blurAmount],
  () => {
    if (backgroundPipeline.value === "webgl2") {
      // bigger the blur amount, smaller the scale between 0.1 and 1
      // blurAmount 0 -> scale 1
      // blurAmount 15 -> scale 0.1

      if (blurAmount.value == 0) {
        scale.value = 1;
        return;
      }
      const blurPercentage = 100 - (blurAmount.value / 15) * 100;

      scale.value = (1 / 100) * blurPercentage;
      if (scale.value < 0.1) {
        scale.value = 0.1;
      }

      console.log("scale", scale.value);
    }
  },
  { immediate: true },
);

export const rawBgBlurState = {
  backgroundActive,
  sigmaColor,
  sigmaSpace,
  blendMode,
  coverageMin,
  coverageMax,
  lightWrapping,
  smoothSegmentationMask,
  backgroundPipeline,
  backgroundSegmentationModel,
  blurAmount,
  scale,
  backgroundOperation,
  backgroundUrl,
  quality,
  backgroundInferenceWorker,
};

function createStore() {
  function setBackgroundSegmentationModel(value) {
    console.log("setBackgroundSegmentationModel", value);
    backgroundSegmentationModel.value = value;
  }

  return {
    backgroundActive,
    sigmaColor,
    sigmaSpace,
    blendMode,
    coverageMin,
    coverageMax,
    lightWrapping,
    smoothSegmentationMask,
    backgroundPipeline,
    backgroundSegmentationModel,
    blurAmount,
    scale,
    backgroundOperation,
    backgroundUrl,
    quality,
    backgroundInferenceWorker,
    setBackgroundSegmentationModel,
    clearBgBlurState,
  };
}

export const useBgBlurState = defineStore("bg-blur-state", createStore);

export const useScopedBgBlurState = (scope: string) => {
  return defineStore("bg-blur-state(" + scope + ")", createStore)();
};
